import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from "sonner";
import { DollarSign, Banknote, Printer, Download } from "lucide-react";
import { exportToCSV, handlePrint } from "../../utils/exportUtils";

const WinningsPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [winnings, setWinnings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlaydays();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchWinnings();
    }
  }, [selectedPlayday]);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data);
      if (response.data.length > 0) {
        setSelectedPlayday(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  const fetchWinnings = async () => {
    try {
      const response = await axios.get(`${API}/results/winnings/${selectedPlayday}`);
      setWinnings(response.data);
    } catch (error) {
      toast.error("Failed to fetch winnings");
    }
  };

  const getSelectedPlaydayName = () => {
    const playday = playdays.find(p => p.id === selectedPlayday);
    return playday ? `${playday.name} (${playday.date})` : "";
  };

  const totalPayout = winnings.reduce((sum, w) => sum + w.total_payout, 0);

  const handleExportWinnings = () => {
    const exportData = winnings.map(winner => ({
      contestant: winner.contestant_name,
      events_won: winner.events.map(e => `${e.event_name}: $${e.payout.toFixed(2)}`).join("; "),
      total_winnings: winner.total_payout.toFixed(2)
    }));
    
    exportToCSV(
      exportData, 
      `winnings_${getSelectedPlaydayName().replace(/[^a-z0-9]/gi, '_')}.csv`,
      ["contestant", "events_won", "total_winnings"]
    );
    toast.success("Winnings exported to CSV");
  };

  const handlePrintWinnings = () => {
    handlePrint("Winnings Report", getSelectedPlaydayName());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container" data-testid="winnings-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Winnings</h1>
          <p className="text-text-secondary mt-1">Total payouts by contestant for selected play day</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintWinnings} disabled={winnings.length === 0} data-testid="print-winnings-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleExportWinnings} disabled={winnings.length === 0} data-testid="export-winnings-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1 className="font-heading font-bold text-2xl">TTP Play Day - Winnings Report</h1>
        <p>{getSelectedPlaydayName()}</p>
        <p className="mt-2">Total Payouts: ${totalPayout.toFixed(2)}</p>
      </div>

      {/* Selector */}
      <div className="flex gap-4 items-end no-print">
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
          <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
            <SelectTrigger data-testid="playday-select">
              <SelectValue placeholder="Select a play day" />
            </SelectTrigger>
            <SelectContent>
              {playdays.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.date})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Card className="border-status-success/30 flex-1 max-w-xs no-print">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-status-success/20 text-status-success">
                <Banknote className="h-5 w-5" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Total Payouts</p>
                <p className="font-heading font-bold text-xl text-status-success">${totalPayout.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Winnings Table */}
      <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading font-bold text-text-primary">Contestant</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Events Won</TableHead>
              <TableHead className="font-heading font-bold text-text-primary text-right">Total Winnings</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {winnings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-text-secondary">
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-text-muted" />
                  No winnings recorded for this play day
                </TableCell>
              </TableRow>
            ) : (
              winnings.map((winner) => (
                <TableRow key={winner.contestant_id} className="hover:bg-surface-muted" data-testid={`winner-${winner.contestant_id}`}>
                  <TableCell className="font-medium text-text-primary">{winner.contestant_name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      {winner.events.map((e, idx) => (
                        <span key={idx} className="px-2 py-1 bg-primary/10 text-text-primary rounded text-xs">
                          {e.event_name}: ${e.payout.toFixed(2)}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono font-bold text-lg text-status-success">
                      ${winner.total_payout.toFixed(2)}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default WinningsPage;
